import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SignedImage } from '../../hooks/useSignedUrl';
import Logo from '../../assets/lumin-logo.png';
import { NavItem } from '../../constants/navigation';

interface AppShellProps {
  title: string;
  description?: string;
  nav: NavItem[];
  actions?: React.ReactNode;
  children: React.ReactNode;
}

const AppShell: React.FC<AppShellProps> = ({ title, description, nav, actions, children }) => {
  const location = useLocation();
  const { loanOfficer, isAdmin, signOut } = useAuth();

  return (
    <div className="min-h-screen bg-[#f4f6fb] text-slate-900 flex">
      <aside className="hidden md:flex md:w-72 lg:w-80 border-r border-lumin-primary/20 bg-lumin-primary flex-col">
        <div className="px-6 py-6 border-b border-white/20 flex items-center gap-3">
          <img src={Logo} alt="Lumin" className="h-8" />
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/70">Lumin</p>
            <p className="text-base font-semibold text-white">Onboarding</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-6 space-y-2">
          {nav.map((item) => {
            const isActive = item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);
            const Icon = item.icon;

            if (item.external) {
              return (
                <a
                  key={item.to}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-200 border border-transparent bg-transparent hover:bg-white/10 text-white/70"
                >
                  <span className="flex items-center gap-3">
                    {item.iconUrl ? (
                      <img src={item.iconUrl} alt="" className="h-7 w-7 object-contain" />
                    ) : Icon ? (
                      <Icon className="h-5 w-5 text-white/60" />
                    ) : null}
                    <span className="font-medium">{item.label}</span>
                  </span>
                  {item.badge !== undefined && (
                    <span className="text-xs font-semibold px-2 py-1 rounded-full bg-white/20 text-white/80">
                      {item.badge}
                    </span>
                  )}
                </a>
              );
            }

            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between px-4 py-3 rounded-2xl transition-all duration-200 border ${
                  isActive
                    ? 'border-white/30 bg-white/20 text-white shadow-sm'
                    : 'border-transparent bg-transparent hover:bg-white/10 text-white/70'
                }`}
              >
                <span className="flex items-center gap-3">
                  {item.iconUrl ? (
                    <img src={item.iconUrl} alt="" className="h-7 w-7 object-contain" />
                  ) : Icon ? (
                    <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-white/60'}`} />
                  ) : null}
                  <span className="font-medium">{item.label}</span>
                </span>
                {item.badge !== undefined && (
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${isActive ? 'bg-white text-lumin-primary' : 'bg-white/20 text-white/80'}`}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-h-screen">
        <header className="border-b border-slate-200 bg-white px-6 lg:px-10 py-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-lumin-primary/70">
              {isAdmin ? 'Workflow Command Deck' : 'Your Launch Plan'}
            </p>
            <h1 className="text-2xl lg:text-3xl font-semibold text-slate-900 mt-2">{title}</h1>
            {description && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{description}</p>}
          </div>
          <div className="flex items-center gap-3">
            {actions}
            {loanOfficer && (
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-sm font-semibold text-slate-900">
                    {loanOfficer.first_name} {loanOfficer.last_name}
                  </p>
                  <p className="text-xs text-slate-500">{isAdmin ? 'Admin' : 'Loan Officer'}</p>
                </div>
                <SignedImage
                  src={loanOfficer.headshot}
                  alt={`${loanOfficer.first_name} ${loanOfficer.last_name}`}
                  className="h-10 w-10 rounded-full object-cover border-2 border-slate-200"
                  fallback={
                    <div className="h-10 w-10 rounded-full bg-lumin-primary flex items-center justify-center text-white font-bold text-sm">
                      {loanOfficer.first_name?.charAt(0)}
                      {loanOfficer.last_name?.charAt(0)}
                    </div>
                  }
                />
                <button
                  onClick={signOut}
                  className="ml-1 px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 px-6 lg:px-10 py-8">{children}</main>
      </div>
    </div>
  );
};

export default AppShell;
